import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { dictionaryList, languageOptions } from '@src/assets/language';
import { languageStorageKey } from '@src/utils/keys';
import { getCurrentLanguage, isSupportedLanguage } from '@src/utils/language';

const initialContext = {
  language: languageOptions.turkey,
  dictionary: dictionaryList.tr,
};

const LanguageContext = createContext(initialContext);

export const LanguageContextProvider = ({ children }) => {
  const [language, setLanguage] = useState(getCurrentLanguage);
  const [dictionary, setDictionary] = useState(() => dictionaryList[language]);

  useEffect(() => {
    const syncLanguage = (event) => {
      if (event.key !== languageStorageKey && event.key !== null) return;
      if (event.storageArea && event.storageArea !== window.localStorage) return;
      const currentLanguage = getCurrentLanguage();
      setLanguage(currentLanguage);
      setDictionary(dictionaryList[currentLanguage]);
    };

    window.addEventListener('storage', syncLanguage);
    return () => window.removeEventListener('storage', syncLanguage);
  }, []);

  const languageChange = useCallback(
    (values) => {
      if (!isSupportedLanguage(values)) {
        throw new RangeError('Unsupported application language.');
      }
      window.localStorage.setItem(languageStorageKey, JSON.stringify(values));
      setLanguage(values);
      setDictionary(dictionaryList[values])
    },

    [setLanguage],
  );

  const dictionaryChange = useCallback((values) => {
    setDictionary(values);
  }, []);

  const value = React.useMemo(
    () => ({
      language,
      languageChange,
      dictionaryChange,
      dictionary,
    }),
    [language, languageChange, dictionaryChange, dictionary],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLang = () => {
  const context = useContext(LanguageContext);
  if (context?.dictionary === undefined) {
    return { ...context, dictionary: {}, language: languageOptions.turkey };
  }
  return context;
};
